
import React, { useEffect, useState } from "react";
import {
    Star,
    Quote,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
} from "lucide-react";

// DEMO CONTENT ONLY.
// Replace with genuine customer testimonials before publishing.

const DEMO_REVIEWS = [
    {
        id: 1,
        name: "Ahmed K.",
        location: "Saudi Arabia",
        rating: 5,
        comment:
            "The booking process was simple, and the hotel options were easy to compare. Everything was clearly explained before confirming the reservation.",
        verified: false,
    },
    {
        id: 2,
        name: "Sarah M.",
        location: "United Kingdom",
        rating: 5,
        comment:
            "I found a hotel in a convenient location for our Dubai trip. The website was easy to use, and the booking details were straightforward.",
        verified: false,
    },
    {
        id: 3,
        name: "Priya S.",
        location: "India",
        rating: 5,
        comment:
            "We were looking for a comfortable hotel within our budget. Comparing the available options helped us make our decision.",
        verified: false,
    },
    {
        id: 4,
        name: "Omar A.",
        location: "United Arab Emirates",
        rating: 4,
        comment:
            "The hotel information was presented clearly, and I could find the details I needed without spending too much time searching.",
        verified: false,
    },
    {
        id: 5,
        name: "Daniel R.",
        location: "Germany",
        rating: 5,
        comment:
            "I liked being able to browse different hotels and check the room information before choosing where to stay.",
        verified: false,
    },
    {
        id: 6,
        name: "Fatima H.",
        location: "Pakistan",
        rating: 5,
        comment:
            "The website made it easy to explore accommodation options for our family trip to Dubai. The layout was clear and simple.",
        verified: false,
    },
];


function getInitials(name = "") {
    return (
        String(name)
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part.charAt(0))
            .join("")
            .toUpperCase() || "G"
    );
}

function TestimonialCard({ review }) {
    const rating = Math.min(
        5,
        Math.max(0, Number(review.rating) || 0)
    );

    return (
        <article
            className="
        group flex h-full flex-col
        rounded-[16px]
        border border-[#E2EDE6]
        bg-white p-6
        shadow-[0_4px_18px_rgba(6,78,59,0.05)]
        transition-all duration-300
        hover:-translate-y-1
        hover:border-[#B9DEC9]
        hover:shadow-[0_14px_32px_rgba(6,78,59,0.11)]
        sm:p-7
      "
        >
            {/* STARS AND QUOTE */}
            <div className="flex items-start justify-between">

                <div
                    role="img"
                    aria-label={`${rating} out of 5 stars`}
                    className="flex items-center gap-1"
                >
                    {Array.from({ length: 5 }, (_, index) => (
                        <Star
                            key={index}
                            size={16}
                            aria-hidden="true"
                            className={
                                index < Math.round(rating)
                                    ? "fill-[#F3AE2C] text-[#F3AE2C]"
                                    : "text-[#D9E2DD]"
                            }
                        />
                    ))}
                </div>


                <Quote
                    size={31}
                    strokeWidth={1.5}
                    className="text-[#CDE9D8]"
                />
            </div>

            {/* REVIEW */}
            <p
                className="
          mt-5 min-h-[110px] flex-1
          text-[14px] leading-[1.9]
          text-[#4F6170]
        "
            >
                "{review.comment}"
            </p>

            <div className="my-5 h-px bg-[#E9F0EB]" />

            {/* GUEST INFO */}
            <div className="flex items-center gap-3">
                {review.avatar ? (
                    <img
                        src={review.avatar}
                        alt=""
                        className="
              h-12 w-12 shrink-0
              rounded-full object-cover
            "
                    />
                ) : (
                    <div
                        className="
              flex h-12 w-12 shrink-0
              items-center justify-center
              rounded-full bg-[#E8F7EE]
              text-sm font-extrabold
              text-[#168B43]
            "
                    >
                        {getInitials(review.name)}
                    </div>
                )}

                <div className="min-w-0 flex-1">
                    <h3
                        className="
              truncate text-[14px]
              font-extrabold text-[#17324D]
            "
                    >
                        {review.name}
                    </h3>

                    <p className="mt-1 text-[11px] text-[#64748B]">
                        {review.location}
                    </p>
                </div>

                {review.verified && (
                    <span
                        className="
              flex items-center gap-1
              text-[10px] font-semibold
              text-[#168B43]
            "
                    >
                        <CheckCircle2 size={15} />
                        <span className="hidden sm:inline">
                            Verified
                        </span>
                    </span>
                )}
            </div>
        </article>
    );
}

export default function Testimonials({
    reviews,
    visibleCount = 3,
}) {
    const [startIndex, setStartIndex] = useState(0);
    const [columns, setColumns] = useState(3);

    // Show demo testimonials when no reviews prop is supplied.
    // An explicitly supplied empty array is treated as no reviews.
    const source = Array.isArray(reviews)
        ? reviews
        : DEMO_REVIEWS;

    const validReviews = source.filter(
        (review) =>
            review &&
            typeof review.comment === "string" &&
            review.comment.trim()
    );

    const count = validReviews.length;

    // RESPONSIVE CARD COUNT
    useEffect(() => {
        const updateColumns = () => {
            if (window.innerWidth < 768) {
                setColumns(1);
            } else if (window.innerWidth < 1024) {
                setColumns(2);
            } else {
                setColumns(3);
            }
        };

        updateColumns();

        window.addEventListener("resize", updateColumns);

        return () =>
            window.removeEventListener("resize", updateColumns);
    }, []);

    const pageSize = Math.min(
        count,
        columns,
        Math.max(1, visibleCount)
    );

    useEffect(() => {
        setStartIndex(0);
    }, [count]);

    const canSlide = count > pageSize;

    useEffect(() => {
        if (!canSlide) return;

        const interval = setInterval(() => {
            setStartIndex((current) => (current + 1) % count);
        }, 4000);

        return () => clearInterval(interval);
    }, [canSlide, count]);

    const visibleReviews = Array.from(
        { length: pageSize },
        (_, index) =>
            validReviews[(startIndex + index) % count]
    );

    const previous = () => {
        if (!count) return;

        setStartIndex(
            (current) => (current - 1 + count) % count
        );
    };

    const next = () => {
        if (!count) return;

        setStartIndex(
            (current) => (current + 1) % count
        );
    };

    return (
        <section
            id="testimonials"
            className="
        bg-[#F5FAF7]
        py-14 sm:py-16 lg:py-20
      "
        >
            {/* SAME WIDTH AS HEADER */}
            <div
                className="
          mx-auto w-full max-w-[1536px]
          px-4 sm:px-6 lg:px-8
        "
            >
                {/* SECTION HEADER */}
                <div
                    className="
            mb-9 flex flex-col
            justify-between gap-5
            sm:flex-row sm:items-end
          "
                >
                    <div className="max-w-[780px]">
                        <p
                            className="
                mb-2 text-[10px] font-extrabold
                uppercase tracking-[0.2em]
                text-[#168B43]
              "
                        >
                            Guest Experiences
                        </p>

                        <h2
                            className="
                text-[28px] font-extrabold
                leading-tight tracking-[-0.04em]
                text-[#17324D]
                sm:text-[36px] lg:text-[40px]
              "
                        >
                            What Our{" "}
                            <span className="text-[#168B43]">
                                Guests Say
                            </span>
                        </h2>

                        <p
                            className="
                mt-3 text-[13px]
                leading-7 text-[#64748B]
                sm:text-[14px]
              "
                        >
                            Discover what travelers have to say
                            about their Dubai experiences.
                        </p>
                    </div>

                    {/* NAVIGATION */}
                    {canSlide && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={previous}
                                aria-label="Previous testimonials"
                                className="
                  flex h-10 w-10 items-center
                  justify-center rounded-full
                  border border-[#CFE4D7]
                  bg-white text-[#064E3B]
                  transition hover:border-[#168B43]
                  hover:bg-[#E8F7EE]
                "
                            >
                                <ArrowLeft size={18} />
                            </button>

                            <button
                                type="button"
                                onClick={next}
                                aria-label="Next testimonials"
                                className="
                  flex h-10 w-10 items-center
                  justify-center rounded-full
                  bg-[#168B43] text-white
                  transition hover:bg-[#087D37]
                "
                            >
                                <ArrowRight size={18} />
                            </button>
                        </div>
                    )}
                </div>

                {/* DEMO NOTICE */}
                {!Array.isArray(reviews) && (
                    <p className="mb-5 text-xs font-medium text-[#64748B]">
                        Demo testimonials — replace with genuine guest
                        reviews before publishing.
                    </p>
                )}

                {/* TESTIMONIAL CARDS */}
                {count > 0 ? (
                    <>
                        <div
                            className="
                grid grid-cols-1 gap-5
                md:grid-cols-2 lg:grid-cols-3
              "
                        >
                            {visibleReviews.map((review, index) => (
                                <TestimonialCard
                                    key={`${review.id ?? index}-${startIndex}`}
                                    review={review}
                                />
                            ))}
                        </div>

                        {/* PAGINATION DOTS */}
                        {canSlide && (
                            <div
                                className="
                  mt-8 flex items-center
                  justify-center gap-2
                "
                            >
                                {validReviews.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => setStartIndex(index)}
                                        aria-label={`Go to testimonial ${index + 1}`}
                                        aria-current={
                                            index === startIndex
                                                ? "true"
                                                : undefined
                                        }
                                        className={`
                      h-2 rounded-full
                      transition-all duration-300
                      ${index === startIndex
                                                ? "w-8 bg-[#168B43]"
                                                : "w-2 bg-[#C8DFD1] hover:bg-[#168B43]"
                                            }
                    `}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div
                        className="
              rounded-[16px]
              border border-[#E2EDE6]
              bg-white px-6 py-12
              text-center
            "
                    >
                        <p className="text-sm text-[#64748B]">
                            No testimonials are available yet.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
